import { useMemo } from 'react'
import type { NextPage, GetStaticPropsContext } from 'next'
import { useTranslations } from 'next-intl'
import {
  Box,
  Divider,
  Text,
  Flex,
  Link,
  useColorModeValue,
} from '@chakra-ui/react'

import { AlertCircleIcon } from '@/components/icons/AlertCircleIcon'
import { ContainerAuthentication } from '@/components/containers/ContainerAuthentication'
import { LoginOptIn } from '@/components/LoginOptIn'
import SeoHead from '@/components/seo-head'
import { Auth0UniversalLogin } from '@/features/auth/Auth0UniversalLogin'
import { KeycloakUniversalLogin } from '@/features/auth/KeycloakUniversalLogin'
import { SignInForm } from '@/features/auth/SignInForm'
import {
  AUTH0_ENABLED,
  KEYCLOAK_ENABLED,
  AUTHENTICATION_PLANNED_UPGRADE,
} from '@/constants'

const AUTHENTICATION_MIGRATION_FAQ_URL =
  'https://support.compassmining.io/hc/en-us/articles/36497523621149-User-Authentication-Migration-FAQs'

const SignInPage: NextPage = () => {
  const t = useTranslations()

  const loginTitle = useMemo(() => {
    return t.rich('auth.sign-in.access-account', {
      bold: (children) => (
        <Text as="span" fontWeight="semibold">
          {children}
        </Text>
      ),
    })
  }, [t])

  return (
    <>
      <SeoHead
        title={t('auth.sign-in.seo.title')}
        breadcrumbTitle={t('auth.sign-in.seo.breadcrumb')}
        description={t('auth.sign-in.seo.description')}
      />
      <Box pt={8}>
        <Flex
          direction={{ base: 'column', lg: 'row' }}
          align={{ base: 'center', lg: 'flex-start' }}
          justify={{ base: 'space-between' }}
          gap={{ md: '40px', xl: '150px' }}
        >
          <Flex
            direction="column"
            align="flex-start"
            justify={{ base: 'space-between' }}
            flexBasis={{ base: '100%', lg: '50%' }}
          >
            <ContainerAuthentication
              boxShadow={'none'}
              w={'100%'}
              px={{ base: 5, md: 4 }}
              justifyContent="center"
              alignItems="flex-end"
            >
              <Text
                lineHeight={{ base: '44px', md: '58px' }}
                fontSize={{ sm: '5xl', base: '3xl' }}
                mb={10}
                as="h1"
              >
                {loginTitle}
              </Text>
              {KEYCLOAK_ENABLED && <KeycloakUniversalLogin />}
              {AUTH0_ENABLED && !KEYCLOAK_ENABLED && <Auth0UniversalLogin />}
              {!AUTH0_ENABLED && !KEYCLOAK_ENABLED && <SignInForm />}
              {AUTHENTICATION_PLANNED_UPGRADE && (
                <Box
                  my={6}
                  py={3}
                  px={4}
                  w="full"
                  bg="orange.100"
                  borderColor="orange.400"
                  borderWidth="1px"
                  rounded="lg"
                  color="gray.700"
                  lineHeight={1.5}
                >
                  <Flex
                    direction="row"
                    alignItems="center"
                    justifyContent="flex-start"
                    mb={1}
                  >
                    <AlertCircleIcon w={5} h={5} color="orange.500" mr={3.5} />
                    <Text fontWeight={700}>
                      {t('auth.sign-in.upgrade-alert.title')}
                    </Text>
                  </Flex>
                  <Text color="yellow.800">
                    {t.rich('auth.sign-in.upgrade-alert.description', {
                      contact: (children) => (
                        <Link
                          href={AUTHENTICATION_MIGRATION_FAQ_URL}
                          isExternal
                          fontWeight={700}
                          textDecoration="underline"
                        >
                          {children}
                        </Link>
                      ),
                    })}
                  </Text>
                </Box>
              )}
            </ContainerAuthentication>
          </Flex>
          <LoginOptIn />
        </Flex>
      </Box>
      <Divider />
    </>
  )
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  return {
    props: {
      messages: (await import(`../../locales/${locale}.json`)).default,
    },
  }
}

export default SignInPage
